import { setHeader, removeHeader, http } from "@/services/http";
import qs from "qs";
import { cloneDeep } from "lodash";
import {
  mapEmergencyDriverFilters,
  mapEmergencyDriverFilterState
} from "@/utils/EmergencyDriverFilterMapping";
import { mapModules } from "@/utils/ModuleMapping";
import { emergencyDriverColumnDefinition } from "@/utils/ColumnMapping";
import { unsetFilters } from "@/utils/FilterUtils";
import saveAs from "file-saver";

/**
 * Store for the state of (led) modules
 *
 * @type Object
 */
const state = {
  inputLock: "emergencyDriverFlux",
  latestChange: "",
  emergencyDriverSearchTerm: null,
  filters: [],
  rawFilters: [],
  emergencyDrivers: [],
  emergencyDriver: null,
  selectedEmergencyDrivers: [],
  emergencyDriverFilters: null,
  initialFilters: [],
  emergencyDriverColumnVisibility: [],
  pinnedEmergencyDrivers: [],
  columnDefs: [],
  inputCalculations: {
    ledArrayVoltage: "",
    maxDriverOutput: "",
    maxLedPower: "",
    emergencyDriverFlux: "",
    emergencyDriverEfficacy: "",
    emergencyDriverPower: ""
  }
};

const getters = {
  initialColumnDefs: (state, getters, rootState) => {
    return emergencyDriverColumnDefinition(rootState.brain.region);
  },
  columnDefs: state => {
    return state.columnDefs;
  },
  emptyInitialFilters(state) {
    return unsetFilters(state.initialFilters);
  },
  rawFilters: state => {
    return state.rawFilters;
  },
  pinnedEmergencyDrivers: state => {
    return state.pinnedEmergencyDrivers;
  },
  emergencyDriver: state => {
    return state.emergencyDriver;
  },
  selectedEmergencyDrivers: state => {
    return state.selectedEmergencyDrivers;
  },
  ledArrayVoltage: state => {
    return state.inputCalculations.ledArrayVoltage;
  },
  maxDriverOutput: state => {
    return state.inputCalculations.maxDriverOutput;
  },
  maxLedPower: state => {
    return state.inputCalculations.maxLedPower;
  },
  emergencyDriverFlux: state => {
    return state.inputCalculations.emergencyDriverFlux;
  },
  emergencyDriverEfficacy: state => {
    return state.inputCalculations.emergencyDriverEfficacy;
  },
  emergencyDriverPower: state => {
    return state.inputCalculations.emergencyDriverPower;
  }
};

const mutations = {
  setColumnDefs: (state, columnDefs) => {
    state.columnDefs = [...columnDefs];
  },

  setFilters: (state, filters) => {
    state.filters = [...filters];
  },

  setRawFilters: (state, rawFilters) => {
    state.rawFilters = rawFilters;
  },

  setEmergencyDriverSearchTerm: (state, searchTerm) => {
    state.emergencyDriverSearchTerm = searchTerm;
  },

  setEmergencyDriverFlux: (state, emergencyDriverFlux) => {
    state.inputCalculations.emergencyDriverFlux = emergencyDriverFlux;
  },

  setEmergencyDriverEfficacy: (state, emergencyDriverEfficacy) => {
    state.inputCalculations.emergencyDriverEfficacy = emergencyDriverEfficacy;
  },

  setEmergencyDriverPower: (state, emergencyDriverPower) => {
    state.inputCalculations.emergencyDriverPower = emergencyDriverPower;
  },

  setLedArrayVoltage: (state, ledArrayVoltage) => {
    state.inputCalculations.ledArrayVoltage = ledArrayVoltage;
  },

  setMaxDriverOutput: (state, maxDriverOutput) => {
    state.inputCalculations.maxDriverOutput = maxDriverOutput;
  },

  setMaxLedPower: (state, maxLedPower) => {
    state.inputCalculations.maxLedPower = maxLedPower;
  },

  resetSpecificFilterGroup: (state, specificFilterGroup) => {
    const targetInitialFilterGroupIndex = state.initialFilters.findIndex(
      initialFilter => initialFilter.title === specificFilterGroup.title
    );
    state.filters[targetInitialFilterGroupIndex] = specificFilterGroup;
  },

  updateFilter: (state, update) => {
    if (!update) return;
    state.filters.forEach(group => {
      group.filters.forEach(filter => {
        if (filter.name === update.name) {
          if (
            filter.name === "driverHousingWidth" ||
            filter.name === "driverHousingLength" ||
            filter.name === "driverHousingHeight"
          ) {
            filter.value = {
              min: update.min,
              max: update.max
            };
          } else if (filter.name === "searchTermFilter") {
            state.emergencyDriverSearchTerm = update.value;
            filter.value = update.value;
          } else {
            filter.value =
              update.values instanceof Array
                ? [...update.values]
                : update.values;
          }
        }
      });
    });
  },

  setInitialFilters: (state, filters) => {
    state.initialFilters = filters;
  },

  setEmergencyDrivers: (state, emergencyDrivers) => {
    state.emergencyDrivers = [...emergencyDrivers];
  },

  setEmergencyDriver: (state, emergencyDriver) => {
    state.emergencyDriver = cloneDeep(emergencyDriver);
    state.emergencyDriverFilters = cloneDeep(state.filters);
  },

  setSelectedEmergencyDrivers: (state, emergencyDriver) => {
    state.selectedEmergencyDrivers = [...emergencyDriver];
  },
  clearCalculatedPerformance: state => {
    state.inputLock = "emergencyDriverFlux";
    state.inputCalculations = {
      ledArrayVoltage: "",
      maxDriverOutput: "",
      maxLedPower: "",
      emergencyDriverFlux: "",
      emergencyDriverEfficacy: "",
      emergencyDriverPower: ""
    };
  },
  setInputLock: (state, lock) => {
    state.inputLock = lock;
  }
};

const actions = {
  async getEmergencyDrivers({ rootState, commit, dispatch }) {
    commit("brain/global/setBusy", true, { root: true });

    // If a solution is chosen use it in the headers
    dispatch("brain/solutions/setHeaders", null, { root: true });

    //The 'filters' are used to be send in the REQUEST
    const filters = mapEmergencyDriverFilterState(state);

    let product = null;
    switch (rootState.brain.region) {
      case "emea":
        removeHeader("X-Client");
        product = "trust-sight";
        break;
      case "na":
        product = "bodine";
        break;
      default:
        commit("brain/global/setBusy", false, { root: true });
        return [];
    }

    const result = await http
      .post(
        "/api/v2/drivers/" +
          product +
          "/data?hasMatchingDriver=true&whitelistingEnabled=true&showArchive=false",
        qs.stringify(filters, { indices: false, arrayFormat: "brackets" }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          }
        }
      )
      .then(response => response.data.data)
      .then(data => {
        // Set Filters
        commit(
          "setFilters",
          mapEmergencyDriverFilters(rootState.brain.region, data.filters)
        );

        // Set Raw Filters
        commit("setRawFilters", data.filters);

        dispatch("brain/global/setTagFamilies", null, { root: true });

        if (!state.initialFilters.length) {
          commit(
            "setInitialFilters",
            mapEmergencyDriverFilters(rootState.brain.region, data.filters)
          );
        }

        const emergencyDrivers = mapModules(rootState.brain.region, data.hits);

        commit("setEmergencyDrivers", emergencyDrivers);

        if (state.emergencyDriver !== null) {
          const activeEmergencyDriver = emergencyDrivers.find(
            emergencyDriver => emergencyDriver.uid === state.emergencyDriver.uid
          );
          if (
            activeEmergencyDriver !== null &&
            activeEmergencyDriver !== undefined
          ) {
            commit("setEmergencyDriver", activeEmergencyDriver);
          }
        }

        setHeader("Client", "EDITv2");
        return emergencyDrivers;
      })
      .catch(err => {
        commit("brain/global/setBusy", false, { root: true });
        if (err.response !== undefined && err.response.status === 401) {
          this._vm.$snotify["error"]("Token expired, please log in again.");
        } else {
          this._vm.$snotify["error"](err);
        }
      });

    // clear busy flag
    commit("brain/global/setBusy", false, { root: true });

    return result;
  },

  disableFilter({ commit, state }, data) {
    let filters = cloneDeep(state.filters);
    filters.map(filter => {
      if (filter.title === "Family") {
        filter.filters[0].availableValues.map(item => {
          if (item.value === data) {
            item.count = 0;
          }
          return item;
        });
      }
      return filter;
    });
    commit("setFilters", filters);
  },

  async handleFilterChange({ dispatch, commit }, { data }) {
    commit("updateFilter", data);
    return await dispatch("getEmergencyDrivers");
  },

  async resetAllFilters({ commit, dispatch, rootState }) {
    commit("setEmergencyDriverSearchTerm", null);
    const rawEmergencyDriverFilters = await http({
      method: "POST",
      url:
        "/api/v2/drivers/bodine/data?hasMatchingDriver=true&whitelistingEnabled=true&showArchive=false&size=0",
      headers: {
        "Content-Type": "multipart/form-data"
      }
    }).then(response => {
      return response.data.data.filters;
    });
    await commit(
      "setFilters",
      mapEmergencyDriverFilters(
        rootState.brain.region,
        rawEmergencyDriverFilters
      )
    );
    return await dispatch("getEmergencyDrivers");
  },

  async downloadEmergencyDrivers({ state, commit }) {
    commit("brain/global/setBusy", true, { root: true });
    // create the filterset to apply
    const filters = mapEmergencyDriverFilterState(state);

    // and request the list
    const result = await http
      .post(
        "/api/v2/drivers/bodine/exportxlsx?hasMatchingDriver=true&whitelistingEnabled=true&showArchive=false",
        qs.stringify(filters, { indices: false, arrayFormat: "brackets" }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          },
          responseType: "arraybuffer"
        }
      )
      .then(response => {
        const blob = new Blob([response.data], {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;"
        });
        const date = new Date();
        const filename = "emergency-drivers-export-" + date.getTime();

        saveAs(blob, filename + ".xlsx");
      })
      .catch(err => {
        if (err.response !== undefined && err.response.status === 401) {
          this._vm.$snotify["error"]("Token expired, please log in again.");
        } else {
          this._vm.$snotify["error"](err);
        }
      });
    commit("brain/global/setBusy", false, { root: true });
    return result;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
